(function(root, factory) {
  var api = factory();
  root.HDCartFetchRecovery = api;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
})(typeof window !== "undefined" ? window : globalThis, function() {
  var DEFAULT_SOFT_RECOVERY_DELAYS_MS = [1500, 3000, 5000];

  function parseInteger(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function normalizeText(value) {
    return String(value == null ? "" : value).toLowerCase();
  }

  function classifyCartFetchFailure(detailsInput) {
    var details = detailsInput && typeof detailsInput === "object" ? detailsInput : {};
    var httpStatus = parseInteger(details.http_status != null ? details.http_status : details.status);
    // 429 = storefront throttle ("retry after backoff"), not a permanent failure. Treat it as a
    // recoverable client transient so soft-recovery's backoff runs, rather than a hard http_failure.
    if (httpStatus === 429) return "client_transient";
    if (httpStatus > 0) return "http_failure";

    var readyState = parseInteger(details.ready_state != null ? details.ready_state : details.readyState);
    var errorName = normalizeText(details.error_name || details.name);
    var errorMessage = normalizeText(details.error_message || details.message);
    var errorHint = normalizeText(details.error_hint);

    var looksLoadFailed = errorMessage.indexOf("load failed") !== -1;
    var looksTypeError = errorName === "typeerror";
    var looksBlocked = errorHint === "request_blocked_or_network_unavailable";
    var hasClientTransientShape = looksBlocked || readyState === 0 || (looksTypeError && looksLoadFailed);

    if (hasClientTransientShape) return "client_transient";
    return "unknown";
  }

  function classifyProductFetchFailure(detailsInput) {
    var details = detailsInput && typeof detailsInput === "object" ? detailsInput : {};
    var httpStatus = parseInteger(details.http_status != null ? details.http_status : details.status);
    // 429 = storefront throttle ("retry after backoff"), not a permanent failure. Treat it as a
    // recoverable client transient so soft-recovery's backoff runs, rather than a hard http_failure.
    if (httpStatus === 429) return "client_transient";
    if (httpStatus > 0) return "http_failure";

    var statusText = normalizeText(details.status_text != null ? details.status_text : details.statusText);
    var errorHint = normalizeText(details.error_hint);
    var looksBlocked = errorHint === "request_blocked_or_network_unavailable";
    var looksAjaxError = statusText === "error" || statusText === "";

    if (looksBlocked && looksAjaxError) return "client_transient";
    return "unknown";
  }

  function analyzeProductFetchFailures(input) {
    var details = input && typeof input === "object" ? input : {};
    var totalHandleCount = Math.max(0, parseInteger(details.totalHandleCount));
    var successfulCount = Math.max(0, parseInteger(details.successfulCount));
    var failedFetches = Array.isArray(details.failedFetches) ? details.failedFetches : [];
    var failedHandles = failedFetches
      .map(function(entry) {
        return entry && entry.handle ? String(entry.handle) : "";
      })
      .filter(function(handle) {
        return handle.length > 0;
      });

    if (successfulCount > 0) {
      return {
        shouldSoftRecover: false,
        failureClass: "partial_success",
        failedHandles: failedHandles,
        failedHandleCount: failedFetches.length,
        totalHandleCount: totalHandleCount,
      };
    }

    var failureClass = "unknown";
    if (failedFetches.length > 0) {
      failureClass = classifyProductFetchFailure(failedFetches[0]);
      for (var index = 1; index < failedFetches.length; index++) {
        var nextClass = classifyProductFetchFailure(failedFetches[index]);
        if (failureClass === "http_failure" || nextClass === "http_failure") {
          failureClass = "http_failure";
          break;
        }
        if (nextClass !== failureClass) {
          failureClass = "unknown";
        }
      }
    }

    return {
      shouldSoftRecover:
        failureClass === "client_transient" &&
        failedFetches.length > 0 &&
        failedFetches.length === totalHandleCount,
      failureClass: failureClass,
      failedHandles: failedHandles,
      failedHandleCount: failedFetches.length,
      totalHandleCount: totalHandleCount,
    };
  }

  function normalizeDelays(delaysMs) {
    if (!Array.isArray(delaysMs) || delaysMs.length === 0) {
      return DEFAULT_SOFT_RECOVERY_DELAYS_MS.slice();
    }
    return delaysMs
      .map(function(value) {
        return Math.max(0, parseInteger(value));
      })
      .filter(function(value) {
        return value > 0;
      });
  }

  function defaultWait(delayMs) {
    return new Promise(function(resolve) {
      setTimeout(resolve, delayMs);
    });
  }

  async function runSoftRecovery(options) {
    var opts = options && typeof options === "object" ? options : {};
    var delays = normalizeDelays(opts.delaysMs);
    var wait = typeof opts.wait === "function" ? opts.wait : defaultWait;
    var runAttempt = typeof opts.runAttempt === "function" ? opts.runAttempt : null;
    var onAttempt = typeof opts.onAttempt === "function" ? opts.onAttempt : null;
    var onFailure = typeof opts.onFailure === "function" ? opts.onFailure : null;
    var lastError = null;

    if (!runAttempt) {
      return {
        status: "exhausted",
        attempt: 0,
        value: null,
        error: new Error("missing_run_attempt"),
      };
    }

    for (var index = 0; index < delays.length; index++) {
      var attempt = index + 1;
      var delayMs = delays[index];

      if (onAttempt) {
        await onAttempt({ attempt: attempt, delayMs: delayMs });
      }

      await wait(delayMs, attempt);

      try {
        var value = await runAttempt(attempt);
        return {
          status: "recovered",
          attempt: attempt,
          value: value,
          error: null,
        };
      } catch (error) {
        lastError = error;
        if (onFailure) {
          await onFailure({ attempt: attempt, delayMs: delayMs, error: error });
        }
      }
    }

    return {
      status: "exhausted",
      attempt: delays.length,
      value: null,
      error: lastError,
    };
  }

  return {
    DEFAULT_SOFT_RECOVERY_DELAYS_MS: DEFAULT_SOFT_RECOVERY_DELAYS_MS,
    classifyCartFetchFailure: classifyCartFetchFailure,
    classifyProductFetchFailure: classifyProductFetchFailure,
    analyzeProductFetchFailures: analyzeProductFetchFailures,
    runSoftRecovery: runSoftRecovery,
  };
});
